import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Container,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Tooltip
} from '@mui/material';
import { 
  ArrowBack, 
  Search, 
  Add, 
  Edit, 
  Delete, 
  CheckCircle, 
  Cancel, 
  Sync, 
  Close,
  Email,
  Phone,
  LocationOn,
  History
} from '@mui/icons-material';
import Loader from './Loader';
import { db } from './Dexie';
import { useLiveQuery } from 'dexie-react-hooks';
import { log } from '../services/loggingService';
import { syncTechyShopUsers } from '../services/userService';

function TechyShopUtenti() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modals state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // User form state
  const [formData, setFormData] = useState({
    email: '',
    nome: '',
    cognome: '',
    telefono: '',
    indirizzo: '',
    citta: '',
    provincia: '',
    cap: '',
    paese: 'Italia',
    newsletter: false,
    email_verificata: false,
    attivo: true,
    password: '',
    confirmPassword: ''
  });
  
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  // Ottieni utenti in tempo reale dal DB
  const users = useLiveQuery(() => db.techyshop_utenti.toArray(), []);

  // Memorizza gli utenti filtrati
  const filteredUsers = useMemo(() => {
    if (!users) return [];
    
    const lowercasedQuery = searchQuery.toLowerCase();
    
    return users.filter(u => {
      const email = u.email || '';
      const nome = u.nome || '';
      const cognome = u.cognome || '';
      const id = u.id ? u.id.toString() : '';
      
      return id.includes(lowercasedQuery) ||
             email.toLowerCase().includes(lowercasedQuery) ||
             nome.toLowerCase().includes(lowercasedQuery) ||
             cognome.toLowerCase().includes(lowercasedQuery);
    });
  }, [searchQuery, users]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email || '',
        nome: user.nome || '',
        cognome: user.cognome || '',
        telefono: user.telefono || '',
        indirizzo: user.indirizzo || '',
        citta: user.citta || '',
        provincia: user.provincia || '',
        cap: user.cap || '',
        paese: user.paese || 'Italia',
        newsletter: user.newsletter == 1 || String(user.newsletter).toLowerCase() === 'true',
        email_verificata: user.email_verificata == 1 || String(user.email_verificata).toLowerCase() === 'true',
        attivo: user.attivo == 1 || String(user.attivo).toLowerCase() === 'true',
        password: '',
        confirmPassword: ''
      });
    } else {
      setSelectedUser(null);
      setFormData({
        email: '',
        nome: '',
        cognome: '',
        telefono: '',
        indirizzo: '',
        citta: '',
        provincia: '',
        cap: '',
        paese: 'Italia',
        newsletter: false,
        email_verificata: false,
        attivo: true,
        password: '',
        confirmPassword: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveUser = async () => {
    if (!formData.email.trim() || !formData.nome.trim() || !formData.cognome.trim()) {
      setToast({ open: true, message: 'Errore: Email, Nome e Cognome sono obbligatori.', severity: 'error' });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setToast({ open: true, message: 'Errore: Le password non coincidono.', severity: 'error' });
      return;
    }

    if (!selectedUser && !formData.password) {
      setToast({ open: true, message: 'Errore: La password è obbligatoria per i nuovi utenti.', severity: 'error' });
      return;
    }

    setIsSaving(true);
    
    const { password, confirmPassword, ...otherData } = formData;
    const userData = {
      ...otherData,
      newsletter: formData.newsletter ? 1 : 0,
      email_verificata: formData.email_verificata ? 1 : 0,
      attivo: formData.attivo ? 1 : 0
    };

    if (password) {
      userData.password_hash = password;
    }

    try {
      if (selectedUser) {
        // Logica di modifica
        const values = Object.keys(userData).map(key => ({
          field: key,
          value: userData[key]
        }));

        const filters = [{ field: 'id', value: selectedUser.id }];

        const response = await fetch('https://deadapi.techysumo.it/techyshop/editUtente', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify({ values, filters }),
        });

        const result = await response.json();

        if (result.result) {
          // Remove password_hash before updating Dexie as it might not be in the schema or we don't want to store it plain
          const { password_hash, ...dexieData } = userData;
          await db.techyshop_utenti.update(selectedUser.id, dexieData);
          setToast({ open: true, message: 'Utente aggiornato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante l'aggiornamento dell'utente");
        }
      } else {
        // Logica di aggiunta
        const response = await fetch('https://deadapi.techysumo.it/techyshop/addUtente', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': process.env.REACT_APP_API_KEY,
          },
          body: JSON.stringify(userData),
        });

        const result = await response.json();

        if (result.result) {
          const newId = result.details.id || result.details;
          const { password_hash, ...dexieData } = userData;
          await db.techyshop_utenti.add({ id: newId, ...dexieData });
          setToast({ open: true, message: 'Utente creato con successo!', severity: 'success' });
          handleCloseModal();
        } else {
          throw new Error(result.text || "Errore durante la creazione dell'utente");
        }
      }
    } catch (error) {
      log("Errore nel salvataggio dell'utente:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente?')) return;

    try {
      const response = await fetch('https://deadapi.techysumo.it/techyshop/delUtente', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': process.env.REACT_APP_API_KEY,
        },
        body: JSON.stringify({ id }),
      });

      const result = await response.json();
      if (result.result) {
        await db.techyshop_utenti.delete(id);
        setToast({ open: true, message: 'Utente eliminato con successo!', severity: 'info' });
      } else {
        throw new Error(result.text || "Errore durante l'eliminazione");
      }
    } catch (error) {
      log("Errore nell'eliminazione dell'utente:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    }
  };

  const handleSync = async () => {
    setIsSaving(true);
    setToast({ open: true, message: 'Sincronizzazione in corso...', severity: 'info' });
    
    try {
      const result = await syncTechyShopUsers(true);
      setToast({ 
        open: true, 
        message: result.message, 
        severity: result.success ? 'success' : 'error' 
      });
    } catch (error) {
      log("Errore durante la sincronizzazione manuale utenti:", 'ERROR', error);
      setToast({ open: true, message: `Errore: ${error.message}`, severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleToastClose = () => setToast({ ...toast, open: false });

  if (!users) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Loader />
        <Typography sx={{ mt: 2 }}>Caricamento utenti...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate('/techyshop')} aria-label="back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ ml: 2, flexGrow: 1 }}>
          Utenti TechyShop
        </Typography>
      </Box>

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button 
          variant="outlined" 
          color="secondary" 
          startIcon={<Sync />} 
          onClick={handleSync}
          disabled={isSaving}
        >
          Sincronizza
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<Add />} 
          onClick={() => handleOpenModal()}
          disabled={isSaving}
        >
          Aggiungi Nuovo Utente
        </Button>
      </Box>

      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          label="Cerca per nome, cognome, email o ID"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Nominativo</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }}>Città</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="center">Stato</TableCell>
              <TableCell sx={{ color: 'primary.main', fontWeight: 'bold' }} align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((u) => (
              <TableRow 
                key={u.id} 
                hover 
                onClick={() => handleOpenModal(u)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' } 
                }}
              >
                <TableCell sx={{ color: 'white' }}>{u.id}</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 600 }}>{u.nome} {u.cognome}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{u.email}</TableCell>
                <TableCell sx={{ color: 'rgba(255,255,255,0.7)' }}>{u.citta} ({u.provincia})</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <Tooltip title={u.attivo == 1 ? "Attivo" : "Non Attivo"}>
                      {u.attivo == 1 ? <CheckCircle style={{ color: '#4dab56' }} fontSize="small" /> : <Cancel style={{ color: '#f44336' }} fontSize="small" />}
                    </Tooltip>
                    {u.email_verificata == 1 && (
                      <Tooltip title="Email Verificata">
                        <Email style={{ color: '#2196f3' }} fontSize="small" />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={(e) => { e.stopPropagation(); handleOpenModal(u); }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={(e) => { e.stopPropagation(); handleDeleteUser(u.id); }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                    Nessun utente trovato.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal Aggiungi/Modifica Utente */}
      <Dialog open={isModalOpen} onClose={handleCloseModal} fullScreen>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton edge="start" color="inherit" onClick={handleCloseModal} aria-label="close" sx={{ mr: 2 }}>
              <Close />
            </IconButton>
            <Typography variant="h6" component="span">
              {selectedUser ? 'Modifica Utente' : 'Aggiungi Nuovo Utente'}
            </Typography>
          </Box>
          {selectedUser && <Chip label={`ID: ${selectedUser.id}`} size="small" />}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Anagrafica</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleInputChange}
                      disabled={isSaving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cognome"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleInputChange}
                      disabled={isSaving}
                    />
                  </Grid>
                </Grid>
                
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Email /></InputAdornment>,
                  }}
                />

                <TextField
                  fullWidth
                  label="Telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Phone /></InputAdornment>,
                  }}
                />

                <Divider sx={{ my: 1 }} />
                
                <Typography variant="subtitle2" color="primary" gutterBottom>Indirizzo di Spedizione/Fatturazione</Typography>
                
                <TextField
                  fullWidth
                  label="Indirizzo"
                  name="indirizzo"
                  value={formData.indirizzo}
                  multiline
                  rows={2}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocationOn /></InputAdornment>,
                  }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Città"
                      name="citta"
                      value={formData.citta}
                      onChange={handleInputChange}
                      disabled={isSaving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <TextField
                      fullWidth
                      label="Prov"
                      name="provincia"
                      value={formData.provincia}
                      inputProps={{ maxLength: 2 }}
                      onChange={handleInputChange}
                      disabled={isSaving}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="CAP"
                      name="cap"
                      value={formData.cap}
                      onChange={handleInputChange}
                      disabled={isSaving}
                    />
                  </Grid>
                </Grid>

                <TextField
                  fullWidth
                  label="Paese"
                  name="paese"
                  value={formData.paese}
                  onChange={handleInputChange}
                  disabled={isSaving}
                />

                <Divider sx={{ my: 1 }} />
                
                <Typography variant="subtitle2" color="primary" gutterBottom>Sicurezza</Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={selectedUser ? "Nuova Password" : "Password"}
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      disabled={isSaving}
                      helperText={selectedUser ? "Lascia vuoto per non cambiare" : ""}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Conferma Password"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      disabled={isSaving}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" color="primary" gutterBottom>Stato Account e Preferenze</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={<Switch name="attivo" checked={formData.attivo} onChange={handleInputChange} color="primary" />}
                    label="Account Attivo"
                    disabled={isSaving}
                  />
                  <FormControlLabel
                    control={<Switch name="email_verificata" checked={formData.email_verificata} onChange={handleInputChange} color="secondary" />}
                    label="Email Verificata"
                    disabled={isSaving}
                  />
                  <FormControlLabel
                    control={<Switch name="newsletter" checked={formData.newsletter} onChange={handleInputChange} color="info" />}
                    label="Iscritto alla Newsletter"
                    disabled={isSaving}
                  />
                </Box>
              </Paper>

              {selectedUser && (
                <>
                  <Typography variant="subtitle2" color="primary" gutterBottom>Dettagli Registrazione</Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History fontSize="small" sx={{ opacity: 0.6 }} />
                        <Box>
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.6 }}>Data Registrazione</Typography>
                          <Typography variant="body2">{selectedUser.data_registrazione ? new Date(selectedUser.data_registrazione).toLocaleString() : 'N/D'}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History fontSize="small" sx={{ opacity: 0.6 }} />
                        <Box>
                          <Typography variant="caption" sx={{ display: 'block', opacity: 0.6 }}>Ultimo Accesso</Typography>
                          <Typography variant="body2">{selectedUser.ultimo_accesso ? new Date(selectedUser.ultimo_accesso).toLocaleString() : 'Mai effettuato'}</Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseModal} disabled={isSaving}>Annulla</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveUser} 
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleToastClose} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default TechyShopUtenti;
